import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Percent, Play, Save } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type PlatformHost = Record<string, any> & {
  default_region?: { slug: string; name: string; currency: string } | null;
  vat_country?: string;
};

type HostChange = {
  id: string;
  changed_at: string;
  changed_by_email: string;
  first: boolean;
  changes: Record<string, { from: string; to: string }>;
};

type BillingRun = {
  id: string;
  billing_period: string;
  status: string;
  dry_run: boolean;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  organizations_considered: number;
  invoices_created: number;
  invoices_skipped: number;
  organizations_failed: number;
  report: Record<string, { organization?: string; status?: string; reason?: string }>;
  error: string;
};

const HOST_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'company_name', label: 'Company name' },
  { key: 'company_id', label: 'Company ID (IČO)' },
  { key: 'vat_id', label: 'VAT ID (DIČ)' },
  { key: 'data_box', label: 'Data box' },
  { key: 'street', label: 'Street' },
  { key: 'district', label: 'District' },
  { key: 'city', label: 'City' },
  { key: 'postal_code', label: 'Postal code' },
  { key: 'country', label: 'Country' },
  { key: 'email', label: 'Contact email' },
  { key: 'phone', label: 'Contact phone' },
  { key: 'website', label: 'Website' },
  { key: 'bank_name', label: 'Bank name' },
  { key: 'bank_account', label: 'Bank account' },
  { key: 'iban', label: 'IBAN' },
  { key: 'swift', label: 'SWIFT / BIC' },
  { key: 'tax_note', label: 'Extra note printed on every invoice' },
];

const HOST_LABEL: Record<string, string> = Object.fromEntries([
  ...HOST_FIELDS.map(({ key, label }) => [key, label]),
  ['vat_payer', 'VAT payer'],
  ['vat_rate_percent', 'VAT rate'],
  ['logo_url', 'Logo'],
]);

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function HostHistory({ entries }: { entries: HostChange[] }) {
  if (entries.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">None yet.</p>;
  }
  return (
    <ol className="space-y-4" data-testid="platform-host-history-list">
      {entries.map((entry) => {
        const count = Object.keys(entry.changes).length;
        return (
          <li key={entry.id} className="rounded-md border p-3" data-testid={`platform-host-change-${entry.id}`}>
            <p className="text-sm">
              <span className="font-medium">{when(entry.changed_at)}</span>
              <span className="text-muted-foreground"> · {entry.changed_by_email || 'a global admin'} · {entry.first ? 'first entered' : `${count} field${count === 1 ? '' : 's'} changed`}</span>
            </p>
            {!entry.first && (
              <ul className="mt-2 space-y-1 text-sm">
                {Object.entries(entry.changes).map(([field, change]) => (
                  <li key={field} className="flex flex-wrap gap-x-2">
                    <span className="w-44 shrink-0 text-muted-foreground">{HOST_LABEL[field] ?? field}</span>
                    <span className="line-through decoration-muted-foreground/60 text-muted-foreground">{change.from || '—'}</span>
                    <span>→ {change.to || '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function BillingPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState<Record<string, string>>({});
  const [period, setPeriod] = React.useState('');
  const [dryRun, setDryRun] = React.useState(true);

  const host = useQuery({
    queryKey: ['platform-host'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/platform-host/');
      return res.data as PlatformHost;
    },
  });
  const hostHistory = useQuery({
    queryKey: ['platform-host-history'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/platform-host/history/')).data as HostChange[];
    },
  });

  const runs = useQuery({
    queryKey: ['billing-runs'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/billing/run/');
      return res.data as BillingRun[];
    },
  });

  React.useEffect(() => {
    if (!host.data) return;
    setForm(
      Object.fromEntries(
        [...HOST_FIELDS.map(({ key }) => key), 'vat_payer', 'vat_rate_percent'].map((key) => [key, String(host.data?.[key] ?? '')]),
      ),
    );
  }, [host.data]);


  const saveHost = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.put('/global-admin/platform-host/', form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-host'] });
      queryClient.invalidateQueries({ queryKey: ['platform-host-history'] });
      toast.success('Platform host saved');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const runBilling = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.post('/global-admin/billing/run/', {
        period: period || undefined,
        dry_run: dryRun,
      });
      return res.data as BillingRun;
    },
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ['billing-runs'] });
      toast.success(
        `${run.billing_period}: ${run.invoices_created} invoiced, ` +
          `${run.invoices_skipped} skipped, ${run.organizations_failed} failed`,
      );
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const complete = Boolean(host.data?.is_complete_for_invoicing);

  return (
    <div className="space-y-6" data-testid="global-billing-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Billing</h2>
        <p className="text-muted-foreground">
          Who the invoices come from, and when the month is run. What anyone is
          charged is set on the Pricing page.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Platform host
            <Badge variant={complete ? 'default' : 'destructive'}>
              {complete ? 'ready' : 'incomplete'}
            </Badge>
          </CardTitle>
          <CardDescription>
            The vendor block on every invoice. Until the company name, ID and
            address are filled in, the monthly run refuses to issue anything —
            an invoice naming nobody is not a valid document.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {host.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {HOST_FIELDS.map(({ key, label }) => (
                  <div key={key} className="space-y-1.5">
                    <Label htmlFor={`host-${key}`}>{label}</Label>
                    <Input
                      id={`host-${key}`}
                      value={form[key] ?? ''}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`host-${key}`}
                    />
                  </div>
                ))}
                <div className="space-y-1.5 sm:col-span-2 rounded-md border p-3" data-testid="host-vat">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label htmlFor="host-vat_payer">Registered for VAT (plátce DPH)</Label>
                      <p className="text-xs text-muted-foreground">
                        {form.vat_payer === 'true'
                          ? `Czech VAT at ${form.vat_rate_percent || '21'}% goes on invoices to ${host.data?.vat_country ?? 'CZ'} customers and to EU customers without a VIES-valid VAT ID. EU businesses with a valid VAT ID are reverse-charged; customers outside the EU are out of scope. Every case is written on the invoice, and VAT is also stated in CZK at the ČNB rate on the date of supply.`
                          : 'No VAT is charged to anyone and every invoice states that the supplier is not registered for VAT.'}
                      </p>
                    </div>
                    <Switch
                      id="host-vat_payer"
                      checked={form.vat_payer === 'true'}
                      onCheckedChange={(on) => setForm({ ...form, vat_payer: on ? 'true' : 'false' })}
                      data-testid="host-vat_payer"
                    />
                  </div>
                  {form.vat_payer === 'true' && (
                    <div className="mt-3 flex items-end gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="host-vat_rate_percent">Standard VAT rate</Label>
                        <div className="relative w-32">
                          <Input
                            id="host-vat_rate_percent"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={form.vat_rate_percent ?? ''}
                            onChange={(e) => setForm({ ...form, vat_rate_percent: e.target.value })}
                            data-testid="host-vat_rate_percent"
                          />
                          <Percent className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <p className="pb-2 text-xs text-muted-foreground">
                        The member state is read from the VAT ID above ({host.data?.vat_country ?? 'CZ'}). The Czech standard rate is 21%.
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Default region</Label>
                  <p className="text-sm" data-testid="host-default-region">
                    {host.data?.default_region
                      ? `${host.data.default_region.name} (${host.data.default_region.currency})`
                      : 'None yet'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    What a customer is invoiced in is decided by their billing address, not set
                    here. Manage it on{' '}
                    <Link to="/pricing/currency-regions" className="underline">
                      Currency regions
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <Button
                onClick={() => saveHost.mutate()}
                disabled={saveHost.isPending}
                data-testid="save-platform-host"
              >
                {saveHost.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card data-testid="platform-host-history">
        <CardHeader>
          <CardTitle>Previous billing details</CardTitle>
        </CardHeader>
        <CardContent>
          {hostHistory.isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <HostHistory entries={hostHistory.data ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Run a month by hand</CardTitle>
          <CardDescription>
            Cron runs the month that just ended on the 1st. This is the same
            thing on demand: billing must never be only automatic.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="billing-period">Period (YYYY-MM)</Label>
              <Input
                id="billing-period"
                placeholder="last month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                data-testid="billing-run-period"
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch id="dry-run" checked={dryRun} onCheckedChange={setDryRun} />
              <Label htmlFor="dry-run">Dry run</Label>
            </div>
            <Button
              onClick={() => runBilling.mutate()}
              disabled={runBilling.isPending}
              data-testid="billing-run-start"
            >
              {runBilling.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
          <CardDescription>
            With no job queue, this is the only place that says whether a month
            actually billed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runs.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (runs.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs recorded yet.</p>
          ) : (
            <div className="space-y-2" data-testid="billing-runs">
              {(runs.data ?? []).map((run) => (
                <details key={run.id} className="rounded-md border p-3">
                  <summary className="flex flex-wrap items-center gap-2 cursor-pointer">
                    <span className="font-medium">{run.billing_period}</span>
                    <Badge variant={run.status === 'succeeded' ? 'default' : 'destructive'}>
                      {run.status}
                    </Badge>
                    {run.dry_run && <Badge variant="outline">dry run</Badge>}
                    <span className="text-xs text-muted-foreground">
                      {run.invoices_created} invoiced · {run.invoices_skipped} skipped ·{' '}
                      {run.organizations_failed} failed · by {run.triggered_by}
                    </span>
                  </summary>
                  <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                    {JSON.stringify(run.report, null, 2)}
                  </pre>
                  {run.error && <p className="mt-2 text-xs text-red-600">{run.error}</p>}
                </details>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
